import { QuickSubmitForm } from "../../components";
import {
  filterConstants,
  teamAndSecurityUrls,
  userAccessPolicyConstants,
} from "../../constants";
import { teamMemberStatus } from "../../enums";
import {
  useQuickSubmitFormHelper,
  useEditItemProperties,
  useGetPageQueryParams,
  useHasAccessClaim,
  useMerchantApiQueryWithParams,
  useNotifications,
  useQueryString,
  useReactRouterLink,
} from "../../hooks";
import {
  IQueryStringNavLinks,
  ITableHeader,
  IViewTeamMember,
  IPageAndSearchRequest,
  PagedMeta,
} from "../../interfaces-and-types";
import { View } from "./View";

export interface ITeamAndSecurityQueryMeta {
  status: string;
}

export interface ITeamAndSecurityViewProps {
  data: IViewTeamMember[];
  headers: ITableHeader[];
  isLoading: boolean;
  isError: boolean;
  totalCount: number;
  queryStringTabs: IQueryStringNavLinks;

  // modals
  openCreateNewModal?: () => void;
  openDetailsModal: (data: any) => void;
  refreshTable: () => void;

  // extra buttons
  onExport: () => void;
}

export const TeamAndSecurity = () => {
  const query = useQueryString();
  const status = query.get(filterConstants.statusKey);
  const { page, page_size, search } = useGetPageQueryParams();

  const notificationsHandler = useNotifications();

  const userHasOwnerClaim = useHasAccessClaim(userAccessPolicyConstants.owner);
  const userHasAdminClaim = useHasAccessClaim(userAccessPolicyConstants.admin);

  const userHasInvitationPermission = userHasOwnerClaim || userHasAdminClaim;

  const { navigate } = useReactRouterLink();

  const {
    isLoading: isLoadingTeamMembers,
    isError: isErrorTeamMembers,
    data: teamMembers,
    meta: teamMembersMeta,
    refetch: refetchTeamMembers,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest & ITeamAndSecurityQueryMeta,
    IViewTeamMember[],
    PagedMeta
  >([
    "getTeamMembers",
    {
      page: (page && Number(page)) || 1,
      page_size: (page_size && Number(page_size)) || 10,
      search,
      status: status || "active",
    },
  ]);

  const headers: ITableHeader[] = [
    {
      key: "initials",
      header: "",
    },
    {
      key: "full_name",
      header: "Name",
    },
    {
      key: "email",
      header: "Email",
    },
    {
      key: "user_access_claims",
      header: "Roles",
    },
    // {
    //   key: "edited_at",
    //   header: "Last Updated",
    // },
    {
      key: "status",
      header: "Status",
    },
    {
      key: "created_at",
      header: "Created",
    },
    {
      key: "more",
      header: "",
    },
  ];

  const queryStringTabs: IQueryStringNavLinks = {
    query: filterConstants.statusKey,
    options: [
      {
        key: "Active",
        value: "active",
      },
      {
        key: "Pending",
        value: "pending",
      },
      {
        key: "Disabled",
        value: "disabled",
      },
    ],
  };

  const onExport = () => {
    alert("exporting data...");
  };

  const refreshTable = () => refetchTeamMembers();

  const showAccessDeniedMessage = () =>
    notificationsHandler.showNotification({
      ariaLabel: "error notification",
      title: "Not Authorized",
      kind: "error",
      message: "You do not have permission to access this resource.",
      statusIconDescription: "error",
      timeout: 10000,
    });

  // insert the functions to open edit/delete modals
  const teamListWithWithLinks: IViewTeamMember[] | undefined = teamMembers?.map(
    (member) => ({
      ...member,
      openEditModal: () => navigate(`${member.id}/details`),
      openDeleteModal: () =>
        !userHasInvitationPermission
          ? showAccessDeniedMessage()
          : member.status === teamMemberStatus.pending
          ? openDeleteInvitationModal(member.id, false)
          : openToggleMemberStatusModal(member.id, member.is_edit_locked),
      resendInvitationLink: () =>
        openResendInvitationLinkModal(member.id, false),
    })
  );

  const openDetailsModal = (id: any) => navigate(`${id}/details`);

  const openAddTeamMemberModal = () =>
    navigate(teamAndSecurityUrls.inviteTeamMember);

  // NOTE: transfer the more complex actions to a details page not <QuickForm />
  const {
    isModalOpen: isToggleMemberStatusModalOpen,
    openModal: openToggleMemberStatusModal,
    closeModal: closeToggleMemberStatusModal,

    isItemDeleteLocked: isTeamMemberEditLocked,

    isLoading: isLoadingToggleMemberStatus,
    isSuccess: isSuccessToggleMemberStatus,
    mutate: toggleTeamMemberStatus,
  } = useQuickSubmitFormHelper("toggleTeamMemberStatus", refreshTable, "PUT");

  const {
    isModalOpen: isDeleteInvitationModalOpen,
    openModal: openDeleteInvitationModal,
    closeModal: closeDeleteInvitationModal,

    isItemDeleteLocked: isInvitationDeleteLocked,

    isLoading: isLoadingDeleteInvitation,
    isSuccess: isSuccessDeleteInvitation,
    mutate: deleteInvitation,
  } = useQuickSubmitFormHelper(
    "deleteTeamMemberInvitation",
    refreshTable,
    "DELETE"
  );

  const {
    isModalOpen: isResendInvitationLinkModalOpen,
    openModal: openResendInvitationLinkModal,
    closeModal: closeResendInvitationLinkModal,

    isItemDeleteLocked: isResendInvitationLinkLocked,

    isLoading: isLoadingResendInvitationLinkItem,
    isSuccess: isSuccessResendInvitationLinkItem,
    mutate: resendInvitationLink,
  } = useQuickSubmitFormHelper("resendInvitationLink", refreshTable, "POST");

  return (
    <>
      <View
        data={teamListWithWithLinks || []}
        headers={headers}
        isLoading={isLoadingTeamMembers}
        isError={isErrorTeamMembers}
        totalCount={teamMembersMeta?.page_meta.item_count || 0}
        queryStringTabs={queryStringTabs}
        onExport={onExport}
        openCreateNewModal={
          userHasInvitationPermission ? openAddTeamMemberModal : undefined
        }
        openDetailsModal={openDetailsModal}
        refreshTable={refreshTable}
      />

      <QuickSubmitForm
        formTitle="Resend Invitation Link"
        buttonText="Re-send Invitation"
        buttonKind="success"
        isModalOpen={isResendInvitationLinkModalOpen}
        closeModal={closeResendInvitationLinkModal}
        isDeleteLocked={isResendInvitationLinkLocked}
        isSubmitting={isLoadingResendInvitationLinkItem}
        isSuccess={isSuccessResendInvitationLinkItem}
        onButtonClick={resendInvitationLink}
        confirmationMessage={
          <>
            <h4>Are you sure you want to re-send the invitation link?</h4>
            <br />
          </>
        }
      />

      <QuickSubmitForm
        formTitle="Delete Team Invite"
        isModalOpen={isDeleteInvitationModalOpen}
        closeModal={closeDeleteInvitationModal}
        isDeleteLocked={isInvitationDeleteLocked}
        isSubmitting={isLoadingDeleteInvitation}
        isSuccess={isSuccessDeleteInvitation}
        onButtonClick={deleteInvitation}
        confirmationMessage={
          <>
            <h4>
              Are you sure you want to delete this invitation to your team?
            </h4>
            <br />

            <span className="text-danger">
              (NOTE: You can send another invitation later.)
            </span>
          </>
        }
      />

      <QuickSubmitForm
        formTitle="Enable/disable team members"
        isModalOpen={isToggleMemberStatusModalOpen}
        closeModal={closeToggleMemberStatusModal}
        isDeleteLocked={isTeamMemberEditLocked}
        isSubmitting={isLoadingToggleMemberStatus}
        isSuccess={isSuccessToggleMemberStatus}
        buttonText={"Toggle Status"}
        onButtonClick={toggleTeamMemberStatus}
        confirmationMessage={
          <>
            <h4>Are you sure you want to disable this member of your team?</h4>
            <br />

            <span className="text-danger">
              (NOTE: You can enable them if you need to later.)
            </span>
          </>
        }
      />
    </>
  );
};
